import React, { useEffect, useState } from 'react';
import { Calendar, Lock, Truck, Loader2 } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { useCompany } from '../../context/CompanyContext';
import { useRole } from '../../context/RoleContext';
import {
  SERVICE_CATEGORIES,
  MODE_ROUTE,
  MODE_APPT,
  subscribeServiceDefaults,
  saveServiceDefaults,
} from '../../utils/serviceDefaults';

/**
 * Scheduling Defaults
 *
 * Per-service-category default for the two-mode scheduling toggle. Used
 * by ScheduleJobModal (one-time jobs) and BookVisitModal (when no program
 * default is set). Recurring programs override this with their own
 * `defaultJobMode`.
 *
 * Owners/admins can edit; everyone else sees read-only.
 */
const SchedulingDefaults = () => {
  const { companyId } = useCompany();
  const { role } = useRole();
  const canEdit = role === 'owner' || role === 'admin';

  const [defaults, setDefaults] = useState({});
  const [draft, setDraft]       = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  // Realtime subscription
  useEffect(() => {
    if (!companyId) return;
    const unsub = subscribeServiceDefaults(companyId, (next) => {
      setDefaults(next);
      // Only seed the draft on the first load — don't clobber user edits
      // when an external update lands while they're mid-edit.
      setDraft((prev) => (Object.keys(prev).length === 0 ? next : prev));
      setLoading(false);
    });
    return () => unsub();
  }, [companyId]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(defaults);

  const setMode = (cat, mode) => {
    setDraft((prev) => ({ ...prev, [cat]: mode }));
  };

  const handleSave = async () => {
    if (!canEdit || !companyId) return;
    setSaving(true);
    try {
      await saveServiceDefaults(companyId, draft);
      // Optimistic local sync: flip isDirty to false immediately rather than
      // waiting for the Firestore snapshot listener to re-emit (which races
      // with key-ordering inconsistencies in JSON.stringify equality).
      setDefaults(draft);
      toast.success('Scheduling defaults saved');
    } catch (err) {
      console.error('[SchedulingDefaults] save error:', err);
      toast.error('Failed to save defaults');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft(defaults);
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-3xl">
        <Skeleton className="h-8 w-1/3" />
        <Card className="p-6 space-y-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12" />)}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="scheduling-defaults-page">
      <div className="flex items-center gap-3">
        <Calendar className="w-6 h-6 text-primary" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Scheduling Defaults</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Pre-select the scheduling mode toggle per service category. Operator can always override per-job.
          </p>
        </div>
      </div>

      <Card className="p-4 sm:p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          How are visits normally scheduled?
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-lg border-2 border-blue-100 bg-blue-50/40 p-3.5">
            <div className="flex items-center gap-2 mb-1 text-sm font-semibold text-blue-700">
              <Lock className="h-4 w-4" />
              Fixed Appointment
            </div>
            <p className="text-[11px] leading-relaxed text-gray-600">
              Customer receives a specific arrival time. Locked from route optimization.
            </p>
          </div>
          <div className="rounded-lg border-2 border-indigo-100 bg-indigo-50/40 p-3.5">
            <div className="flex items-center gap-2 mb-1 text-sm font-semibold text-indigo-700">
              <Truck className="h-4 w-4" />
              Route Stop
            </div>
            <p className="text-[11px] leading-relaxed text-gray-600">
              Customer receives a service date only. Dispatch and Build Day determine the route sequence and ETA later.
            </p>
          </div>
        </div>
        <p className="text-[11px] text-gray-500 mt-3">
          Recurring service programs carry their own default — these category defaults only apply to one-time jobs scheduled outside a program.
        </p>
      </Card>

      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Service Categories</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {SERVICE_CATEGORIES.map((cat) => {
            const current = draft[cat.value];
            return (
              <div
                key={cat.value}
                className="flex items-center justify-between px-5 py-3.5"
                data-testid={`scheduling-defaults-row-${cat.value}`}
              >
                <span className="text-sm font-medium text-gray-900">{cat.label}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => canEdit && setMode(cat.value, MODE_APPT)}
                    disabled={!canEdit}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border transition ${
                      current === MODE_APPT
                        ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    } ${!canEdit ? 'cursor-not-allowed opacity-60' : ''}`}
                    data-testid={`scheduling-defaults-${cat.value}-appointment`}
                  >
                    <Lock className="w-3 h-3" />
                    Fixed Appointment
                  </button>
                  <button
                    type="button"
                    onClick={() => canEdit && setMode(cat.value, MODE_ROUTE)}
                    disabled={!canEdit}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border transition ${
                      current === MODE_ROUTE
                        ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-medium'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    } ${!canEdit ? 'cursor-not-allowed opacity-60' : ''}`}
                    data-testid={`scheduling-defaults-${cat.value}-route`}
                  >
                    <Truck className="w-3 h-3" />
                    Route Stop
                  </button>
                  {!current && (
                    <span className="text-[10px] text-gray-400 italic ml-1">unset</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {canEdit && (
        <>
          <Separator />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={!isDirty || saving}
              data-testid="scheduling-defaults-reset-btn"
            >
              Reset
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="scheduling-defaults-save-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : (
                'Save Defaults'
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default SchedulingDefaults;
